/**
 * The reviews API from the browser, shared by the reviews section and the
 * shelf panel's „publish the note“ control (#107).
 *
 * Both sit on a book's page and show the same review from two sides, so a
 * change made in one has to reach the other: `reviewsChanged` announces it,
 * and `useReviewsChanged` listens. A plain in-page signal, not a store: the
 * only thing it carries is "reload this book's reviews".
 */

import { useEffect } from 'react'
import type { ApiResponse } from '../../shared/api.ts'
import type { MessageKey } from '../../shared/message-keys.ts'

export const reviewsBase = (world: string, work: string) => `/api/reviews/${encodeURIComponent(world)}/${encodeURIComponent(work)}`

export async function send(method: string, path: string, payload?: unknown): Promise<{ ok: true; data: unknown } | { ok: false; key: MessageKey }> {
  try {
    const response = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: payload === undefined ? {} : { 'content-type': 'application/json' },
      ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
    })
    const body = (await response.json().catch(() => null)) as ApiResponse<unknown> | null
    if (body?.ok === true) return { ok: true, data: body.data }
    return { ok: false, key: body !== null && !body.ok ? body.error.key : 'error.unexpected' }
  } catch {
    return { ok: false, key: 'error.unexpected' }
  }
}

type Listener = (book: string) => void
const listeners = new Set<Listener>()
const bookKey = (world: string, work: string) => `${world}/${work}`

/** Tell every listener on this page that a book's reviews have changed. */
export function reviewsChanged(world: string, work: string): void {
  const book = bookKey(world, work)
  for (const listener of listeners) listener(book)
}

/** Call `onChange` whenever this book's reviews are changed elsewhere on the page. */
export function useReviewsChanged(world: string, work: string, onChange: () => void): void {
  useEffect(() => {
    const book = bookKey(world, work)
    const listener: Listener = (changed) => {
      if (changed === book) onChange()
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [world, work, onChange])
}
